import { ref } from "vue";
import DealerKit from "../../../api";
import Moment from "moment";
import Bus from "../../../events";
import { reportApiError } from "../../../utils";
/**
 * Create an empty finance settlement.
 */
const createEmptyFinanceSettlement = () => {
    return {
        id: undefined,
        agreement_number: undefined,
        finance_company: undefined,
        amount: undefined,
        due_at: undefined,
        payment_method: undefined,
        paid_at: undefined,
        stock_listing: undefined,
    };
};
/**
 * Finance Settlement Ref
 * Used for storing the global state of the settlement.
 */
const settlement = ref(createEmptyFinanceSettlement());
export function useSettlementState() {
    /**
     * Load the current settlement from the backend.
     *
     * @param id
     */
    function loadSettlement(id) {
        return DealerKit.financeSettlements.showFinanceSettlement(id).then(response => {
            return patchSettlement(response.data.data);
        });
    }
    /**
     * Create or update a settlement on the backend.
     *
     * @param settlement
     */
    function saveSettlement(settlement) {
        return new Promise((resolve, reject) => {
            let operation;
            if (settlement.id) {
                operation = DealerKit.financeSettlements.updateFinanceSettlement(settlement.id, {
                    finance_company: settlement.finance_company?.id,
                    amount: settlement.amount,
                    due_at: settlement.due_at ? Moment(settlement.due_at).format() : undefined,
                    agreement_number: settlement.agreement_number,
                    payment_method: settlement.payment_method ?? null,
                    paid_at: settlement.paid_at ?? null,
                });
            }
            else {
                operation = DealerKit.financeSettlements.storeFinanceSettlement({
                    finance_company: settlement.finance_company?.id,
                    amount: settlement.amount,
                    due_at: settlement.due_at ? Moment(settlement.due_at).format() : undefined,
                    agreement_number: settlement.agreement_number,
                    stock_listing: settlement.stock_listing,
                    payment_method: settlement.payment_method,
                    paid_at: settlement.paid_at,
                });
            }
            operation
                .then(response => {
                patchSettlement(response.data.data);
                Bus.emit('finance.settlement.saved');
                resolve(response.data);
            })
                .catch(e => {
                reject(e);
                reportApiError(e);
            });
        });
    }
    /**
     * Destroy the current settlement.
     */
    function destroySettlement() {
        return new Promise((resolve, reject) => {
            if (confirm('Are you sure you want to remove this finance settlement?')) {
                const settlementId = settlement.value.id;
                DealerKit.financeSettlements.destroyFinanceSettlement(settlementId).then(() => {
                    resetSettlementState();
                    resolve(null);
                }).catch(e => {
                    reportApiError(e);
                    reject(e);
                });
            }
            else {
                resolve(null);
            }
        });
    }
    /**
     * Merge properties into the current finance settlement.
     *
     * @param attributes
     */
    function patchSettlement(attributes = {}) {
        return settlement.value = mergeSettlements(attributes, settlement.value);
    }
    /**
     * Reset the stores state.
     */
    function resetSettlementState() {
        patchSettlement({
            ...createEmptyFinanceSettlement(),
            stock_listing: { id: settlement.value?.stock_listing?.id }
        });
    }
    /**
     * Merge the values from one settlement to another.
     *
     * @param value
     * @param settlement
     */
    function mergeSettlements(value, settlement = null) {
        const returnSettlement = settlement ?? createEmptyFinanceSettlement();
        return { ...returnSettlement, ...value };
    }
    /**
     * Export our public API.
     */
    return {
        settlement,
        patchSettlement,
        resetSettlementState,
        loadSettlement,
        saveSettlement,
        destroySettlement,
        createEmptyFinanceSettlement,
        mergeSettlements
    };
}
